import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, RocketLaunch, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { DomainCheckButton } from "@/components/domaincheck/DomainCheckButton";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { PageLinks } from "./PageLinks";
import { StepShell, setupUrl } from "./StepShell";

const INVITE = {
  invited: (i) => `Login invite emailed to ${i.email}`, linked_existing: (i) => `Linked their existing login ${i.email}`,
  already_linked: (i) => `${i.email} already has a login`, skipped: () => "No login invite sent", failed: (i) => `Invite failed: ${i.detail}`,
};

function Checklist({ steps, onPick }) {
  return (
    <ul className="border-2 border-black divide-y divide-zinc-200" data-testid="setup-live-checklist">
      {steps.map((s) => (
        <li key={s.key} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`setup-live-check-${s.key}`}>
          {s.done ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : <WarningCircle size={16} weight="fill" className="text-red-600" />}
          <span className="font-bold">{s.label}</span>
          <span className="flex-1 min-w-0 text-zinc-600 truncate">{s.done ? "Done" : s.note}</span>
          {!s.done && <button type="button" className={BTN_WHITE} onClick={() => onPick(s.key)} data-testid={`setup-live-fix-${s.key}`}>Fix</button>}
        </li>
      ))}
    </ul>
  );
}

function Login({ login, email, invite, setInvite }) {
  if (login.status !== "none") {
    return <div className="text-xs" data-testid="setup-login-status">Login: {login.status === "active" ? "they have signed in" : "invited, not signed in yet"} ({login.emails.join(", ")})</div>;
  }
  return (
    <label className="flex items-center gap-2 text-xs">
      <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} data-testid="setup-send-invite" />
      Email a login invite to {email || "their contact email"}
    </label>
  );
}

export function GoLiveStep({ state, orgId, onSaved, onPick }) {
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${setupUrl(orgId)}/go-live`, { send_invite: invite });
      setSent(data.invite);
      onSaved(data);
      toast.success(`${data.org.name} is live`);
    } catch (e) {
      toast.error(errText(e, "Couldn't go live"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <StepShell title="Go live" testId="setup-step-live" hint="Publishes their buyer page so matching leads from their pages reach them."
               footer={!state.live && (
                 <button type="button" className={BTN_BLACK} disabled={busy || !state.ready} onClick={go} data-testid="setup-go-live">
                   <RocketLaunch size={11} weight="bold" /> {busy ? "Going live…" : "Go live"}
                 </button>
               )}>
      {state.live ? (
        <div className="border-2 border-black bg-emerald-600 text-white p-3 text-sm font-bold flex items-center gap-2" data-testid="setup-live-banner">
          <CheckCircle size={18} weight="fill" /> Live. Matching leads now reach {state.org.name}.
        </div>
      ) : <Checklist steps={state.steps.slice(0, 4)} onPick={onPick} />}
      {!state.live && !state.ready && <p className="text-xs text-red-700" data-testid="setup-go-live-blocked">Finish the steps above first. A test lead has to pass before going live.</p>}
      <Login login={state.login} email={state.org.contact_email} invite={invite} setInvite={setInvite} />
      {sent && <div className={`text-xs ${sent.status === "failed" ? "text-red-700" : ""}`} data-testid="setup-invite-result">{(INVITE[sent.status] || (() => sent.status))(sent)}</div>}
      <PageLinks links={state.links} live={state.live} />
      {state.live && (
        <div className="flex flex-wrap gap-2">
          <DomainCheckButton body={{ org_id: orgId }} testId="setup-domain-check" className={BTN_WHITE} />
          <Link to={`/clients/${orgId}/manage`} className={BTN_WHITE} data-testid="setup-open-manage">Open Manage buyer</Link>
        </div>
      )}
    </StepShell>
  );
}
